import { Component, OnInit } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CaseAgentFlowSessionDataComponent } from './case-agent-flow-session-data/case-agent-flow-session-data.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { CaseAgentFlowSessionsService } from '../../_services/case-agent-flow-sessions.service';
import { FilesService } from '../../../common/_services/files.service';
import { CaseAgentFlowSession } from '../../_types/case-agent-flow-session';
import { ComunicationsComponent } from 'src/app/modules/comunications/communications/comunications.component';
import { CasesService } from '../../_services/cases.service';
import { CasesFilesService } from '../../_services/cases-files.service';
import { Case } from '../../_types/case';
import { CaseFile } from '../../_types/case-file';

@Component({
    selector: 'app-case-agent-flow-session',
    templateUrl: './case-agent-flow-session.component.html',
    styleUrls: ['./case-agent-flow-session.component.scss'],
    standalone: false
})
export class CaseAgentFlowSessionComponent implements OnInit {
    public sessions: CaseAgentFlowSession[] = [];
    public flows: any[] = [];
    //informes periciales generados por la IA para este expediente, uno por sesión
    public informeFiles: CaseFile[] = [];
    public selectedFlowId: string | null = null;
    public loading = false;
    public requesting = false;
    public deleting = false;
    public case?: Case;

    //el tipo de daño del expediente (caseTypeId) es el claimCategory que se manda al crear la
    //reclamación, así que sin él no se puede solicitar una videoperitación
    get hasDamageType(): boolean {
        return this.case?.caseTypeId != null && this.case.caseTypeId !== '';
    }

    constructor(
        private config: DynamicDialogConfig,
        private dynamicDialogRef: DynamicDialogRef,
        private caseAgentFlowSessionsService: CaseAgentFlowSessionsService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private dialogService: DialogService,
        private filesService: FilesService,
        private casesService: CasesService,
        private casesFilesService: CasesFilesService
    ) {}

    statusLabel(status?: string): string {
        switch (status) {
            case 'completed': return 'Completado';
            case 'available': return 'Disponible';
            case 'in_progress': return 'En progreso';
            case 'escalated': return 'Escalado';
            case 'expired': return 'Expirado';
            case 'error': return 'Error de procesado';
            case 'new':
            case undefined:
            case '': return 'Pendiente';
            default: return status;
        }
    }

    isExpired(session: CaseAgentFlowSession): boolean {
        if (!session.expiresAt) return false;
        const status = session.status || 'new';
        if (status !== 'new' && status !== 'in_progress') return false;
        return new Date(session.expiresAt).getTime() < Date.now();
    }

    effectiveStatus(session: CaseAgentFlowSession): string {
        return this.isExpired(session) ? 'expired' : (session.status || 'new');
    }

    statusSeverity(session: CaseAgentFlowSession): 'success' | 'info' | 'warn' | 'danger' {
        const status = this.effectiveStatus(session);
        switch (status) {
            case 'completed': return 'success';
            case 'escalated':
            case 'error':
            case 'expired': return 'danger';
            case 'available':
            case 'in_progress': return 'warn';
            default: return 'info';
        }
    }

    flowName(flowId?: string): string {
        if (!flowId) return '';
        const flow = this.flows.find(f => f.flowId === flowId);
        return flow?.name ?? flowId;
    }

    //metadata es un sobre { recopilacion, informe, sessionStartedAt, sessionCompletedAt, consent }
    //que rellena el worker. Lo parseamos una vez y lo cacheamos en la propia sesión.
    private parseMeta(session: CaseAgentFlowSession): any {
        const s: any = session;
        if (s._parsedMeta !== undefined) return s._parsedMeta;
        try {
            s._parsedMeta = session.metadata ? JSON.parse(session.metadata) : null;
        } catch {
            s._parsedMeta = null;
        }
        return s._parsedMeta;
    }

    //cap. 1 · F. sesión: rango horario de la sesión (inicio a fin), leído de metadata
    sessionRange(session: CaseAgentFlowSession): string | null {
        const m = this.parseMeta(session);
        const start = m?.sessionStartedAt;
        const end = m?.sessionCompletedAt;
        if (!start && !end) return null;
        const d = (v: string) => new Date(v);
        const fmtDateTime = (v: string) => d(v).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        const fmtTime = (v: string) => d(v).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        if (start && end) {
            //mismo día: fecha una vez + "hora a hora"; distinto día: ambas completas
            const sameDay = d(start).toDateString() === d(end).toDateString();
            return sameDay ? `${fmtDateTime(start)} a ${fmtTime(end)}` : `${fmtDateTime(start)} a ${fmtDateTime(end)}`;
        }
        return fmtDateTime(start || end);
    }

    //cap. 1 · consentimiento: true/false/null (desconocido), leído de metadata
    consentGiven(session: CaseAgentFlowSession): boolean | null {
        const m = this.parseMeta(session);
        return (m && typeof m.consent === 'boolean') ? m.consent : null;
    }

    //true solo si consta explícitamente que NO se dio consentimiento
    noConsent(session: CaseAgentFlowSession): boolean {
        return this.consentGiven(session) === false;
    }

    downloadEvidenceFile(session: CaseAgentFlowSession) {
        if (!session.fileId) return;
        this.filesService.download(session.fileId);
    }

    //el informe pericial no se guarda en la sesión: se localiza por el nombre del fichero,
    //que el servicio en background genera como ia_informe_pericial_{sessionId}.pdf
    informeFileId(session: CaseAgentFlowSession): string | null {
        if (!session.sessionId) return null;
        const fileName = `ia_informe_pericial_${session.sessionId}.pdf`;
        const caseFile = this.informeFiles.find(cf => cf.file?.fileName === fileName);
        return caseFile?.fileId ?? null;
    }

    downloadInformeFile(session: CaseAgentFlowSession) {
        const fileId = this.informeFileId(session);
        if (!fileId) return;
        this.filesService.download(fileId);
    }

    async ngOnInit() {
        await Promise.all([this.loadCase(), this.loadSessions(), this.loadFlows(), this.loadInformeFiles()]);
    }

    async loadCase() {
        this.case = await firstValueFrom(this.casesService.get(this.config.data.id));
    }

    async loadSessions() {
        this.loading = true;
        try {
            const filter = {
                caseId: new Filter(FilterOperation.equals, 'caseId', '', this.config.data.id)
            };
            const params: IParamsCollection = {
                filter: FilterBuilder.fromObject(filter).build()
            };
            const response = await firstValueFrom(this.caseAgentFlowSessionsService.all(params));
            this.sessions = response.data;
        } finally {
            this.loading = false;
        }
    }

    async loadInformeFiles() {
        try {
            const filter = {
                caseId: new Filter(FilterOperation.equals, 'caseId', '', this.config.data.id),
                fileName: new Filter(FilterOperation.startsWith, 'file.fileName', '', 'ia_informe_pericial_')
            };
            const params: IParamsCollection = {
                filter: FilterBuilder.fromObject(filter).build(),
                include: ['file']
            };
            const response = await firstValueFrom(this.casesFilesService.all(params));
            this.informeFiles = response.data;
        } catch {
            //sin informes no hay botón de descarga, pero el listado debe seguir funcionando
            this.informeFiles = [];
        }
    }

    async loadFlows() {
        try {
            this.flows = await firstValueFrom(this.caseAgentFlowSessionsService.getFlows());
        } catch {
            this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'No se pudieron cargar los flujos disponibles' });
        }
    }

    async requestSession() {
        if (!this.selectedFlowId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Selecciona un flujo antes de solicitar' });
            return;
        }
        if (!this.hasDamageType) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'El expediente no tiene tipo de daño asignado' });
            return;
        }
        this.requesting = true;
        try {
            await firstValueFrom(this.caseAgentFlowSessionsService.requestSession(this.config.data.id, this.selectedFlowId));
            this.messageService.add({ severity: 'success', summary: 'OK', detail: 'Videoperitación solicitada correctamente' });
            this.selectedFlowId = null;
            await this.loadSessions();
        } catch {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo solicitar la videoperitación' });
        } finally {
            this.requesting = false;
        }
    }

    confirmDelete(session: CaseAgentFlowSession) {
        this.confirmationService.confirm({
            message: '¿Estás seguro de que deseas eliminar esta videoperitación?',
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => this.deleteSession(session)
        });
    }

    async deleteSession(session: CaseAgentFlowSession) {
        this.deleting = true;
        try {
            await firstValueFrom(this.caseAgentFlowSessionsService.delete(session.id));
            this.messageService.add({ severity: 'success', summary: 'OK', detail: 'Videoperitación eliminada correctamente' });
            await this.loadSessions();
        } catch {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la videoperitación' });
        } finally {
            this.deleting = false;
        }
    }

    copyUrl(url: string) {
        navigator.clipboard.writeText(url);
        this.messageService.add({ severity: 'info', summary: '', detail: 'URL copiada al portapapeles' });
    }

    openSessionData(session: CaseAgentFlowSession) {
        this.dialogService.open(CaseAgentFlowSessionDataComponent, {
            closable: true,
            modal: true,
            header: 'Datos de la videoperitación',
            width: '70%',
            contentStyle: { 'max-height': '80vh', overflow: 'auto' },
            baseZIndex: 10000,
            data: { metadata: session.metadata }
        });
    }

    close() {
        this.dynamicDialogRef.close();
    }

    async addComunication(session: CaseAgentFlowSession) {
        let _case:Case = await firstValueFrom(this.casesService.get(this.config.data.id, { include: ['client'] }) );
        let comunicationModel:any = {
            session: session,
            _case: _case
        };
        let ref = this.dialogService.open(ComunicationsComponent, {
            closable: true,
            modal: true,
            header: 'Enviar comunicación',
            width: '60%',
            draggable: true,
            data: {
                caseId: this.config.data.id,
                model: comunicationModel,
                //el enlace se manda por SMS con la plantilla de videoperitación ya cargada
                comunicationType: 'SMS',
                templateCode: 'AGENT_VIDEO_FLOW',
                templateMissingWarning: 'No existe plantilla de SMS para videoperitación (código AGENT_VIDEO_FLOW). Redacte el mensaje manualmente.',
                email: _case?.client?.email,
                phoneNumber: _case?.client?.phone1,
                templateDestination: 'Case'
            }
        });

        ref.onClose.subscribe((data: any) => {
            console.log('Comunication dialog closed with data:', data);
        });
    }
}
